import { observer } from 'mobx-react-lite';
import React from 'react';
import { SelfServiceLoginHint } from '#/lib/SelfServiceLoginHint';
import { getBrandForRequest } from '#/lib/brand';
import jucyData from '@jucy-nakuru/common/services/jucyData';
import clientConfig from '@jucy-nakuru/config/clientConfig';
import { NextPage } from 'next';
import Head from 'next/head';
import App from '../../components/App';
import { HomeContent, type HomeContentProps, HomeLayout, MaintenanceMessage } from '../../components/Pages/Home';
import URLUtil from '../../lib/URLUtil';
import auth0 from '../../services/auth0';
import { GetServerSideProps } from '../_app';

const Home: NextPage<HomeContentProps> = observer(function HomeComponent(props) {
    return (
        <App layout={HomeLayout}>
            <Head>
                <title>Home</title>
                <meta property="og:title" content="Home" key="title" />
            </Head>
            {clientConfig.maintenance.showMessage ? <MaintenanceMessage /> : <HomeContent {...props} />}
        </App>
    );
});
Home.displayName = 'Home';

export const getServerSideProps: GetServerSideProps<{ content: string }> = async (context) => {
    const brand = getBrandForRequest(context.req);
    if (await auth0?.isLoggedIn(context.req, context.res)) {
        const destination = URLUtil.stringToUrl(
            context.query.returnTo ? (context.query.returnTo as string) : '/self-service/manage-bookings',
            brand
        ).toString();
        return {
            redirect: {
                destination,
                permanent: false,
            },
        };
    }
    const loginHint = SelfServiceLoginHint.getLoginHint(context);
    if (loginHint) {
        SelfServiceLoginHint.saveLoginHint({ hint: loginHint, context });
        const qs = context?.query ? new URLSearchParams(context.query as unknown as URLSearchParams).toString() : '';
        return {
            redirect: {
                destination: `/self-service/login${qs ? `?${qs}` : ''}`,
                permanent: false,
            },
        };
    }
    const [welcome, welcomeTitle, intro, heroLabel, loginButtonLabel] = await Promise.all([
        jucyData.fetchContentHtml(`/${brand}/nz/pages-home-welcome`),
        jucyData.fetchContent(`/${brand}/nz/pages-home-welcome-title`).then((data) => data.content),
        jucyData.fetchContentHtml(`/${brand}/nz/pages-home-intro`),
        jucyData.fetchContent(`/${brand}/nz/pages-home-hero-label`).then((data) => data.content),
        jucyData.fetchContent(`/${brand}/nz/pages-home-login-button-label`).then((data) => data.content),
    ]);

    return {
        props: {
            content: welcome || '',
            contentTitle: welcomeTitle || '',
            heroLabel: heroLabel || 'My booking',
            intro: intro || '',
            loginButtonLabel: loginButtonLabel || 'Log in',
        },
    };
};
export default Home;
